import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["name", "subject", "syllabus", "timeAgo", "progressBar"];
  static values = {
    url: String,
    interval: { type: Number, default: 6000 } // 6 seconds per request
  };

  connect() {
    if (window.recentRequestsDismissed === true) {
      return;
    }

    if (sessionStorage.getItem("recentRequestsCTAClicked") === "true") {
      window.recentRequestsDismissed = true;
      sessionStorage.removeItem("recentRequestsCTAClicked");
      return;
    }

    this.requests = [];
    this.currentIndex = 0;
    this.paused = false;
    
    // Animation state
    this.elapsedTime = 0;
    this.duration = this.intervalValue;
    this.lastTimestamp = null;
    this.animationFrameId = null;
    this.showing = false;
    
    // Bind methods
    this.tick = this.tick.bind(this);
    
    this.fetchRequests();
  }

  disconnect() {
    this.stopTimer();
  }

  fetchRequests() {
    fetch(this.urlValue)
      .then(response => {
        if (!response.ok) throw new Error("Network response was not ok");
        return response.json();
      })
      .then(data => {
        if (data && data.length > 0) {
          this.requests = data;
          this.showNextRequest();
        }
      })
      .catch(error => {
        console.error("Error fetching recent tutor requests:", error);
      });
  }

  showNextRequest() {
    if (this.requests.length === 0) return;

    const request = this.requests[this.currentIndex];
    
    // Populate data
    this.nameTarget.textContent = request.name;
    this.subjectTarget.textContent = request.subject;
    this.syllabusTarget.textContent = request.syllabus || "General";
    this.timeAgoTarget.textContent = request.time_ago;
    
    // Increment index
    this.currentIndex = (this.currentIndex + 1) % this.requests.length;
    
    // Slide up/fade in
    this.showToast();
  }

  showToast() {
    this.showing = true;
    this.elapsedTime = 0;
    this.lastTimestamp = null;
    
    // Ensure starting at 100%
    this.progressBarTarget.style.transform = "scaleX(1)";
    
    // Show toast
    this.element.classList.remove("hidden");
    
    // Force layout reflow to allow transition to occur
    this.element.offsetHeight;
    
    this.element.classList.remove("translate-y-10", "opacity-0");
    this.element.classList.add("translate-y-0", "opacity-100");
    
    // Start progress timer
    this.startTimer();
  }

  hideToast() {
    this.showing = false;
    this.stopTimer();
    
    // Hide toast
    this.element.classList.remove("translate-y-0", "opacity-100");
    this.element.classList.add("translate-y-10", "opacity-0");
    
    // Wait for transition to complete, then add hidden and show next
    setTimeout(() => {
      if (!this.showing) {
        this.element.classList.add("hidden");
        if (this.requests.length > 0) {
          this.showNextRequest();
        }
      }
    }, 500); // matches the duration-500 class
  }

  startTimer() {
    this.paused = false;
    this.lastTimestamp = performance.now();
    this.animationFrameId = requestAnimationFrame(this.tick);
  }

  stopTimer() {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  pause() {
    if (this.showing) {
      this.paused = true;
      this.stopTimer();
    }
  }

  resume() {
    if (this.showing && this.paused) {
      this.paused = false;
      this.lastTimestamp = performance.now();
      this.animationFrameId = requestAnimationFrame(this.tick);
    }
  }

  close(event) {
    if (event) {
      if (event.currentTarget && event.currentTarget.tagName === "A") {
        // Do not prevent default for anchor tags so they can navigate
        try {
          const url = new URL(event.currentTarget.href, window.location.href);
          const isSamePage = url.pathname === window.location.pathname && url.search === window.location.search;
          if (!isSamePage) {
            sessionStorage.setItem("recentRequestsCTAClicked", "true");
          }
        } catch (e) {
          // Ignore parsing errors
        }
      } else {
        event.preventDefault();
      }
      event.stopPropagation();
    }
    
    window.recentRequestsDismissed = true;
    
    this.showing = false;
    this.stopTimer();
    
    this.element.classList.remove("translate-y-0", "opacity-100");
    this.element.classList.add("translate-y-10", "opacity-0");
    
    setTimeout(() => {
      this.element.classList.add("hidden");
    }, 500);
  }

  tick(timestamp) {
    if (this.paused || !this.showing) return;

    if (!this.lastTimestamp) {
      this.lastTimestamp = timestamp;
    }
    
    const elapsed = timestamp - this.lastTimestamp;
    this.lastTimestamp = timestamp;
    
    this.elapsedTime += elapsed;
    
    // Update progress bar scale-x
    const progress = Math.max(0, 1 - (this.elapsedTime / this.duration));
    this.progressBarTarget.style.transform = `scaleX(${progress})`;
    
    if (this.elapsedTime >= this.duration) {
      this.hideToast();
    } else {
      this.animationFrameId = requestAnimationFrame(this.tick);
    }
  }
}
